import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";
import type { ForceDisplayMode } from "@/shared/lib/forcePc";

export type DisplaySettingsEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/display-value-changed"
      | "settings/display-mode-changed"
      | "settings/display-dialog-changed"
      | "settings/display-dialog-confirmed";
  }
>;

export const DISPLAY_SETTING_KEYS = [
  "deleteKey",
  "darkMode",
  "privacyMode",
  "globalFontScale",
  "spaceMode",
  "jukeboxEnabled",
  "showR18",
  "postBodyFontSize",
  "ogpPreviewEnabled",
  "externalEmbedEnabled",
  "imageClickOpensNewTab",
  "aiImageMetadataEnabled",
  "threadAiSummaryEnabled",
  "fontFamily",
  "displayDensity",
  "threadImageScale",
  "headerFixed",
  "extendedTextEffects",
  "wheelReloadEnabled",
  "replyButtonFixed",
  "newRepliesBannerWidth",
  "newRepliesBannerPosition",
  "catalogFontScale",
  "columns",
  "rows",
  "textLines",
  "imageSize",
  "openInNewTab",
  "showNew",
  "showCount",
  "showUnreadCount",
  "catalogAnim",
  "autoReload",
  "catalogHoverPreview",
  "catalogHoverScale",
  "threadMenuOpenMethod",
] as const;

export type DisplaySettingKey = (typeof DISPLAY_SETTING_KEYS)[number];

function isDisplaySettingKey(value: string): value is DisplaySettingKey {
  return (DISPLAY_SETTING_KEYS as readonly string[]).includes(value);
}

export interface DisplaySettingsState {
  readonly forceDisplayMode: ForceDisplayMode;
  readonly showClearHistoryDialog: boolean;
  readonly showClearAllDataDialog: boolean;
}

export type DisplaySettingsEffect =
  | {
      readonly type: "set-value";
      readonly key: DisplaySettingKey;
      readonly value: boolean | number | string | null;
    }
  | { readonly type: "set-display-mode"; readonly value: ForceDisplayMode }
  | { readonly type: "clear-history" }
  | { readonly type: "clear-all-data" };

export function createDisplaySettingsMediator(
  forceDisplayMode: ForceDisplayMode,
  runEffect: (effect: DisplaySettingsEffect) => void,
): Mediator<DisplaySettingsState, DisplaySettingsEvent, DisplaySettingsEffect> {
  return createMediator<
    DisplaySettingsState,
    DisplaySettingsEvent,
    DisplaySettingsEffect
  >(
    {
      forceDisplayMode,
      showClearHistoryDialog: false,
      showClearAllDataDialog: false,
    },
    (state, event) => {
      switch (event.type) {
        case "settings/display-value-changed":
          if (!isDisplaySettingKey(event.key)) return { status: "unhandled" };
          return {
            status: "handled",
            state,
            effects: [
              { type: "set-value", key: event.key, value: event.value },
            ],
          };
        case "settings/display-mode-changed":
          if (event.value === state.forceDisplayMode) {
            return { status: "handled", state };
          }
          return {
            status: "handled",
            state: { ...state, forceDisplayMode: event.value },
            effects: [{ type: "set-display-mode", value: event.value }],
          };
        case "settings/display-dialog-changed":
          return {
            status: "handled",
            state: {
              ...state,
              [event.dialog === "clear-history"
                ? "showClearHistoryDialog"
                : "showClearAllDataDialog"]: event.open,
            },
          };
        case "settings/display-dialog-confirmed":
          return {
            status: "handled",
            state: {
              ...state,
              [event.dialog === "clear-history"
                ? "showClearHistoryDialog"
                : "showClearAllDataDialog"]: false,
            },
            effects: [
              {
                type:
                  event.dialog === "clear-history"
                    ? "clear-history"
                    : "clear-all-data",
              },
            ],
          };
      }
    },
    { runEffect },
  );
}
